import { useEffect, useRef, useState } from "react";
import { Activity, Lock, RefreshCw } from "lucide-react";
import api from "@/lib/api";

// Mirrors backend strategy_registry STRATEGY_FAMILY long_vol set — UOA is only
// relevant as a directional/catalyst context signal for Long-Vol strategies.
const LONG_VOL = new Set([
  "bull_call_spread", "bear_put_spread", "long_call", "long_put",
  "protective_put", "risk_reversal", "calendar_spread", "diagonal_spread",
  "long_straddle", "long_strangle",
]);

const FLAG = "#F59E0B";
const QUIET = "var(--hoip-text-tertiary)";

function Row({ testid, label, value, flagged, title }) {
  return (
    <div className="flex items-start justify-between gap-2 text-[10px] border-b pb-1"
      style={{ borderColor: "var(--hoip-divider)" }} data-testid={testid} title={title}>
      <span className="flex items-center gap-1.5 shrink-0" style={{ color: "var(--hoip-text-secondary)" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: flagged ? FLAG : "rgba(113,113,122,0.5)" }} />
        {label}
      </span>
      <span className="font-mono-data text-right" style={{ color: flagged ? FLAG : QUIET }}>{value}</span>
    </div>
  );
}

export const UOAPanel = ({ symbol, strategy, expiry }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const clickedRef = useRef(false);
  const isLongVol = LONG_VOL.has(strategy);

  useEffect(() => {
    if (!symbol || !isLongVol) return;
    setLoading(true);
    api.get(`/uoa/${symbol}`, { params: expiry ? { expiry } : {} })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [symbol, isLongVol, expiry]);

  if (!isLongVol) return null;

  const onSweepClick = () => {
    if (clickedRef.current) return;
    clickedRef.current = true;
    api.post("/uoa/sweep-lock-click", { ticker: symbol }).catch(() => {});
  };

  const jumpToChain = (f) => {
    window.dispatchEvent(new CustomEvent("ml-uoa-jump", {
      detail: { strike: f.strike, type: f.type, expiry: f.expiry || data?.expiry_scanned },
    }));
  };

  const b = data?.baseline;
  const vo = data?.vol_oi;
  const vb = data?.volume_vs_baseline;
  const iv = data?.iv_change;
  const baselineDiffers = data?.baseline_basis_expiry && data.baseline_basis_expiry !== data.expiry_scanned;
  const nearestNote = baselineDiffers ? ` (tracks nearest expiry ${data.baseline_basis_expiry})` : "";

  return (
    <div className="border rounded-sm p-2.5 space-y-1.5" data-testid="uoa-panel"
      style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-[10px] font-bold" style={{ color: "var(--hoip-text-primary)" }}>
          <Activity className="w-3 h-3" style={{ color: FLAG }} />
          Unusual Options Activity
          <span className="font-normal text-[9px]" style={{ color: QUIET }}>
            {data?.expiry_scanned ? `expiry ${data.expiry_scanned}` : "informational"}
          </span>
        </div>
        {b && !b.mature && (
          <span className="text-[8px] px-1.5 py-0.5 rounded-sm" data-testid="uoa-baseline-indicator"
            style={{ color: QUIET, background: "var(--hoip-hover)", border: "1px dashed var(--hoip-border)" }}
            title={`The volume baseline self-accumulates one snapshot per trading day for this ticker. Flags become fully mature at ${b.target} days of history.`}>
            Baseline still building · Day {b.day_display}/{b.target}
          </span>
        )}
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-[10px] py-2" style={{ color: QUIET }}>
          <RefreshCw className="w-3 h-3 animate-spin" /> Scanning chain…
        </div>
      ) : !data || !data.data_available ? (
        <p className="text-[9px] py-1" style={{ color: QUIET }} data-testid="uoa-no-data">
          No option volume data for {symbol}{data?.expiry_scanned ? ` on the ${data.expiry_scanned} expiry` : ""} from the current source — Vol/OI flags unavailable for this expiry.
        </p>
      ) : (
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-2 text-[10px] border-b pb-1"
            style={{ borderColor: "var(--hoip-divider)" }} data-testid="uoa-vol-oi-row"
            title={`Contracts on the ${data.expiry_scanned} expiry trading ≥${vo?.threshold}× their open interest — a proxy for new positioning rather than unwinds. Click a flag to jump the Options Chain to that row.`}>
            <span className="flex items-center gap-1.5 shrink-0" style={{ color: "var(--hoip-text-secondary)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: vo?.flagged ? FLAG : "rgba(113,113,122,0.5)" }} />
              Vol/OI
            </span>
            {vo?.flagged ? (
              <span className="flex flex-col items-end gap-0.5">
                {vo.top.map((f) => (
                  <button key={`${f.strike}-${f.type}`} onClick={() => jumpToChain(f)}
                    data-testid={`uoa-flag-jump-${f.strike}-${f.type}`}
                    className="font-mono-data text-right cursor-pointer hover:underline decoration-dotted underline-offset-2"
                    style={{ color: FLAG, background: "none", border: "none", padding: 0 }}
                    title={`Jump the Options Chain to the $${f.strike} ${f.type} row (${f.expiry || data.expiry_scanned})`}>
                    {f.ratio}× — ${f.strike}{f.type === "call" ? "C" : "P"} (vol {f.volume.toLocaleString()} / OI {f.open_interest.toLocaleString()}) ↗
                  </button>
                ))}
              </span>
            ) : (
              <span className="font-mono-data" style={{ color: QUIET }}>quiet</span>
            )}
          </div>
          <Row testid="uoa-baseline-row" label="Volume vs 90d avg"
            flagged={vb?.flagged}
            title={`Today's total nearest-expiry option volume vs this ticker's self-accumulated daily baseline (flags at ≥${vb?.threshold}×).${nearestNote}`}
            value={vb?.ratio != null
              ? `${vb.ratio}× (${(vb.total_volume ?? data.total_volume).toLocaleString()} vs ${vb.baseline_avg?.toLocaleString()} avg)${baselineDiffers ? " · nearest exp" : ""}`
              : `${(vb?.total_volume ?? data.total_volume).toLocaleString()} today — baseline building`} />
          <Row testid="uoa-iv-row" label="IV Δ (d/d)"
            flagged={iv?.flagged}
            title={`Nearest-expiry ATM IV change vs the previous snapshot. Flags only when the move is ≥${iv?.threshold_pts} pts AND volume is elevated — concentration of both is what separates signal from noise.${nearestNote}`}
            value={iv?.delta_pts != null
              ? `${iv.delta_pts > 0 ? "+" : ""}${iv.delta_pts} pts${iv.flagged ? ` alongside ${iv.volume_context}× volume — market repricing expected movement` : ""}${baselineDiffers ? " · nearest exp" : ""}`
              : "no prior snapshot yet"} />
          <div className="flex items-center justify-between gap-2 text-[10px] pb-0.5 cursor-help select-none"
            data-testid="uoa-sweep-locked" onClick={onSweepClick}
            title="Sweep detection classifies aggressive multi-exchange orders from intraday tick data — not derivable from end-of-day chain snapshots. Requires a market-data tier upgrade; tracked as a known gap, not a missing feature.">
            <span className="flex items-center gap-1.5" style={{ color: QUIET, opacity: 0.7 }}>
              <Lock className="w-2.5 h-2.5" />
              Sweep detection
            </span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-sm" style={{ color: QUIET, background: "var(--hoip-hover)", opacity: 0.8 }}>
              requires intraday data
            </span>
          </div>
        </div>
      )}

      <p className="text-[8px] italic leading-snug" style={{ color: QUIET }} data-testid="uoa-caveat">
        Unusual volume is not inherently directional — a spike can reflect a calendar spread, relative-value
        trade, or hedge, not necessarily a directional bet. Supporting context, not a standalone signal.
      </p>
    </div>
  );
};
